import React, { useState, useEffect, useRef } from 'react'
import {
  Layout,
  BookOpen,
  FileText,
  Sparkles,
} from 'lucide-react'
import SiteContextEditor from '@/components/SiteContextEditor'
import ComposantSection, { ComposantData } from '@/components/ComposantSection'
import LookAndFeelSection, { LookAndFeelData } from '@/components/LookAndFeelSection'
import Page, { PageData } from '@/components/Page'
import PageSlider from '@/components/PageSlider'
import AddPageButton from '@/components/AddPageButton'
import UserInput from '@/components/UserInput'
import { useAuthFetch } from '@/App'
import GenerationProgress from '../components/GenerationProgress'

// -----------------------------------------------------------------------------
// Helpers & constants
// -----------------------------------------------------------------------------

const INITIAL_PAGE = (): PageData => ({
  id: Math.random().toString(36).slice(2, 10),
  name: 'NewPage',
  description: '',
  local_components: [],
})

const INITIAL_LOOK_AND_FEEL = (): LookAndFeelData => ({
  id: Math.random().toString(36).slice(2, 10),
  description: '',
  images: [],
})

function findNodeByIndex(nodes, targetIndex) {
  for (const node of nodes) {
    if (node.index === targetIndex) {
      return node;
    }
    if (node.has_children && node.inputs && Array.isArray(node.inputs.children)) {
      const found = findNodeByIndex(node.inputs.children, targetIndex);
      if (found) return found;
    }
  }
  return null;
}


// -----------------------------------------------------------------------------
// Main component
// -----------------------------------------------------------------------------

export default function SiteBuilder() {
  // ---------------------------------------------------------------------------
  // State – editing
  // ---------------------------------------------------------------------------
  const [siteContext, setSiteContext] = useState('')
  const [composantsGlobaux, setComposantsGlobaux] = useState<ComposantData[]>([])
  const [pages, setPages] = useState<PageData[]>([
    {
      ...INITIAL_PAGE(),
      name: 'HomePage',
      description: ``,
    },
  ])
  const [lookAndFeel, setLookAndFeel] = useState(INITIAL_LOOK_AND_FEEL)
  const [selectedPage, setSelectedPage] = useState(0)
  const [isEditing, setIsEditing] = useState(false)

  // ---------------------------------------------------------------------------
  // State – generation & logs
  // ---------------------------------------------------------------------------
  type GenerationState = 'idle' | 'waitingForPlan' | 'executing'
  const [generationState, setGenerationState] = useState<GenerationState>('idle')
  const generationStateRef = useRef<GenerationState>('idle')
  useEffect(() => {
    generationStateRef.current = generationState
  }, [generationState])

  const [totalSteps, setTotalSteps] = useState(0)
  const totalStepsRef = useRef(0)
  const [currentStep, setCurrentStep] = useState(0)
  const [currentTask, setCurrentTask] = useState('')
  const planRef = useRef<any>(null) // garde le plan pour extraire les titres

  const [logLines, setLogLines] = useState<any[]>([])
  const [logsVisible, setLogsVisible] = useState(true)

  // Auth fetch
  const authFetch = useAuthFetch()

  // Convenience helpers
  const resetGeneration = () => {
    setGenerationState('idle')
    setTotalSteps(0)
    setCurrentStep(0)
    setCurrentTask('')
    planRef.current = null
  }

  const appendLogLine = (line: any) => {
    setLogLines((prev) => [...prev, line])
  }

  // ---------------------------------------------------------------------------
  // CRUD helpers
  // ---------------------------------------------------------------------------
  const handleAddPage = () => {
    const newPage = INITIAL_PAGE()
    setPages((prev) => [...prev, newPage])
    setSelectedPage(pages.length)
  }

  const handleUpdatePage = (index: number, updated: PageData) => {
    setPages((prev) => prev.map((p, i) => (i === index ? updated : p)))
  }

  const handleDeletePage = (index: number) => {
    setPages((prev) => prev.filter((_, i) => i !== index))
    setSelectedPage((prev) =>
      prev > 0 && prev === index ? prev - 1 : Math.min(prev, pages.length - 2),
    )
  }

  // ---------------------------------------------------------------------------
  // Generation logic
  // ---------------------------------------------------------------------------
  const handleGenerate = async () => {
    setLogLines([])
    setGenerationState('waitingForPlan')
    setCurrentTask('Préparation du plan…')

    try {
      const response = await authFetch(`${import.meta.env.VITE_BACKEND_BASE_URL}/generate_site`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          global_context: siteContext,
          global_components: composantsGlobaux,
          look_and_feel: lookAndFeel,
          pages,
        }),
      })

      if (!response.ok || !response.body) {
        const errorText = await response.text()
        throw new Error(errorText || 'Erreur lors de la génération du site.')
      }

      const reader = response.body.getReader()
      const decoder = new TextDecoder()
      let buffer = ''
      let done = false

      while (!done) {
        const { value, done: doneReading } = await reader.read()
        done = doneReading
        if (value) {
          buffer += decoder.decode(value, { stream: true })
          let lines = buffer.split('\n')
          buffer = lines.pop() || ''

          for (let line of lines) {
            if (!line.trim()) continue
            processEvent(line)
          }
        }
      }

      if (buffer.trim()) processEvent(buffer)
    } catch (error: any) {
      alert('Erreur lors de la génération du site :\n' + (error?.message || error))
    } finally {
      resetGeneration()
    }
  }

  /**
   * Handle a single NDJSON line coming from the backend.
   */
  const processEvent = (rawLine: string) => {
    try {
      const json = JSON.parse(rawLine)
      appendLogLine(json)

      if (json.event === 'plan_generated' && json.plan) {
        const metrics = json.plan.metrics
        const subPlan = json.plan.sub_plans?.[0]
        planRef.current = subPlan
        const steps = metrics.steps || 0;
        setTotalSteps(steps)
        totalStepsRef.current = steps
        setCurrentStep(0)
        setGenerationState('executing')
        return
      }

      if (generationStateRef.current !== 'executing') return

      if (json.event === 'node_start' && json.node_index) {
        const planNodes = planRef.current?.plan || [];
        const node = findNodeByIndex(planNodes, json.node_index);
        setCurrentTask(node?.title || node?.name || '');
      }

      if (json.event === 'node_finish' && json.node_index != null) {
        setCurrentStep(prev => Math.max(prev, json.node_index + 1));
      }

      if (json.event === 'project_uploaded' && json.url) {
        setGenerationState('idle')
        setCurrentTask('')
        window.location.href = `/sitebuilder/preview?url=${encodeURIComponent(json.url)}`
      }

    } catch (e) {
      console.error('Chunk mal formé :', rawLine)
    }
  }

  // ---------------------------------------------------------------------------
  // UserInput result
  // ---------------------------------------------------------------------------
  const handleUserInputResult = (result: {
    global_context: string
    pages: Array<{ name: string; description: string; local_components: Array<{ name: string; description: string }> }>
    global_components: Array<{ name: string; description: string }>
    look_and_feel: string
  }) => {
    setSiteContext(result.global_context || '')
    setLookAndFeel((prev) => ({ ...prev, "description": result.look_and_feel || '' }))
    setComposantsGlobaux(
      (result.global_components || []).map((c, i) => ({
        id: Math.random().toString(36).slice(2, 10) + i,
        name: c.name,
        description: c.description,
      })),
    )

    setPages(
      (result.pages || []).map((p, i) => ({
        id: Math.random().toString(36).slice(2, 10) + i,
        name: p.name,
        description: p.description,
        local_components: (p.local_components || []).map((c, j) => ({
          id: Math.random().toString(36).slice(2, 10) + j,
          name: c.name,
          description: c.description,
        })),
      })),
    )
    setSelectedPage(0)
    setIsEditing(true)
  }

  // ---------------------------------------------------------------------------
  // Render helpers
  // ---------------------------------------------------------------------------
  const progressPct = totalSteps > 0 ? Math.min((currentStep / totalSteps) * 100, 100) : 0
  const disabled = generationState !== 'idle'

  // ---------------------------------------------------------------------------
  // JSX
  // ---------------------------------------------------------------------------
  return (
    <main className="min-h-screen bg-white flex flex-col py-6 md:py-10">
      {generationState !== "idle" && (
        <GenerationProgress
          state={generationState}
          currentStep={currentStep}
          totalSteps={totalSteps}
          currentTask={currentTask}
          plan={planRef.current}
        />
      )}

      <div className="container flex flex-col gap-8 md:gap-12">
        <header className="flex flex-wrap items-center gap-3 md:gap-6 mb-2">
          <img
            src="https://cdn.brixel.ai/logo/brixel_logo_full.png"
            alt="Brixel SiteBuilder"
            className="h-10 w-auto max-w-[160px] self-start md:self-center select-none"
            draggable={false}
          />

          <h1 className="text-2xl md:text-3xl text-brixel font-extrabold tracking-tight text-gray-900 flex items-center gap-2">
            <Sparkles className="w-6 h-6 text-brixel" />
            <span className="font-bold text-brixel">SiteBuilder</span>
          </h1>

          <div className="ml-auto flex items-center gap-4">
            <button
              onClick={handleGenerate}
              disabled={disabled || siteContext.trim() === ''}
              className={`inline-flex items-center gap-2 px-5 py-2 rounded-xl text-sm font-semibold transition 
                ${disabled || siteContext.trim() === ''
                  ? 'bg-gray-200 text-gray-400 shadow-none opacity-60 cursor-not-allowed border border-gray-300'
                  : 'bg-brixel text-white shadow-lg hover:scale-105 hover:bg-brixel-dark hover:shadow-xl cursor-pointer border border-brixel'
                }
              `}
            >
              <Sparkles className="w-4 h-4" /> Générer le site
            </button>
            <span className="text-sm text-gray-400 hidden md:flex items-center gap-2">
              <span className="inline-flex items-center gap-1">
                <FileText className="w-4 h-4 text-blue-brixel" /> Contexte global
              </span>
              <span className="mx-2 text-gray-300">|</span>
              <span className="inline-flex items-center gap-1">
                <BookOpen className="w-4 h-4 text-yellow-brixel" /> Composants globaux
              </span>
              <span className="mx-2 text-gray-300">|</span>
              <span className="inline-flex items-center gap-1">
                <Layout className="w-4 h-4 text-brixel" /> Pages
              </span>
            </span>
          </div>
        </header>

        {!isEditing ? (
          <>
            <div className="flex items-center justify-end mb-4">
              <button
                onClick={() => setIsEditing(true)}
                className="text-sm text-brixel hover:underline"
              >
                Passer en mode édition
              </button>
            </div>
            <UserInput onResult={handleUserInputResult} className="mb-2" />
          </>
        ) : (
          <div className="flex flex-col lg:flex-row gap-8">
            <aside className="flex flex-col gap-6 w-full lg:w-1/3 lg:max-w-md flex-shrink-0 opacity-100">
              <SiteContextEditor
                value={siteContext}
                onChange={setSiteContext}
                minRows={6}
                maxRows={16}
                disabled={disabled}
              />
              <LookAndFeelSection
                lookAndFeel={lookAndFeel}
                onChange={setLookAndFeel}
                disabled={disabled}
              />
              <ComposantSection
                composants={composantsGlobaux}
                onChange={setComposantsGlobaux}
                title="Composants globaux"
                emptyLabel="Aucun composant global pour le moment."
                addLabel=""
                disabled={disabled}
              />
            </aside>

            <section className="flex-1 flex flex-col gap-6 overflow-auto">
              <div className="flex items-center gap-3 mb-2">
                <Layout className="w-5 h-5 text-brixel" />
                <h2 className="font-semibold text-lg md:text-xl text-gray-900 flex-1">
                  Pages du site
                </h2>
                <AddPageButton onClick={handleAddPage} disabled={disabled} />
              </div>
              <PageSlider<PageData>
                pages={pages}
                selectedIndex={selectedPage}
                onSelect={setSelectedPage}
                renderPage={(page, i, selected) => (
                  <Page
                    page={page}
                    onChange={(upd) => handleUpdatePage(i, upd)}
                    onDelete={pages.length > 1 ? () => handleDeletePage(i) : undefined}
                    selected={selected}
                    className="w-full max-w-md"
                    disabled={disabled}
                  />
                )}
                pageWidth={380}
                responsive
                showArrows
              />
            </section>
          </div>
        )}
      </div>

      {logLines.length > 0 && (
        <section
          className={`fixed bottom-0 left-0 right-0 z-[1500] bg-gray-900 text-white text-sm shadow-lg border-t border-gray-700 transition-transform duration-300 ${logsVisible ? 'translate-y-0' : 'translate-y-[calc(100%-2rem)]'
            }`}
          style={{ maxHeight: logsVisible ? '15rem' : '2rem' }}
        >
          <div className="flex items-center justify-between px-4 py-2 border-b border-gray-800 bg-gray-950">
            <h3 className="font-bold text-brixel">Logs de génération</h3>
            <button
              onClick={() => setLogsVisible((prev) => !prev)}
              className="text-xs text-white bg-brixel px-2 py-1 rounded hover:bg-brixel-dark transition"
            >
              {logsVisible ? 'Replier' : 'Déplier'}
            </button>
          </div>
          {logsVisible && (
            <pre className="px-4 py-2 whitespace-pre-wrap break-words overflow-y-auto max-h-[11rem]">
              {logLines.map((line, i) => (
                <div key={i}>
                  {line.event ? (
                    <>
                      <strong>{line.event}</strong>: {line.node_name || line.title || ''}
                    </>
                  ) : (
                    JSON.stringify(line)
                  )}
                </div>
              ))}
            </pre>
          )}
        </section>
      )}
    </main>
  )
}
